import React, { useState, useEffect } from 'react';
import { useAuth, apiClient } from '../auth/AuthContext';

interface User {
  id: string;
  username: string;
  email: string;
  role: 'admin' | 'manager' | 'tenant';
  buildingId: string;
  createdAt: string;
  updatedAt: string;
}
interface PricingRule {
  id: string;
  buildingId: string;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  hourlyRate: number;
  createdAt: string;
  updatedAt: string;
}

interface NewPricingRule {
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  hourlyRate: number;
}

interface NewUser {
  username: string;
  email: string;
  password: string;
  role: 'admin' | 'manager' | 'tenant';
}

const DAYS_OF_WEEK = [
  'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'
];

const AdminPage: React.FC = () => {
  const { user, token } = useAuth();
  const [activeTab, setActiveTab] = useState<'users' | 'pricing'>('users');
  const [users, setUsers] = useState<User[]>([]);
  const [pricingRules, setPricingRules] = useState<PricingRule[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // User management states
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUser, setNewUser] = useState<NewUser>({
    username: '',
    email: '',
    password: '',
    role: 'tenant',
  });

  // Pricing management states
  const [showAddPricing, setShowAddPricing] = useState(false);
  const [newPricing, setNewPricing] = useState<NewPricingRule>({
    dayOfWeek: 1,
    startTime: '09:00',
    endTime: '17:00',
    hourlyRate: 50,
  });


  const fetchUsers = async () => {
    if (!token) return;

    try {
      setLoading(true);
      const data = await apiClient.get<User[]>('/admin/users');
      setUsers(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const fetchPricingRules = async () => {
    if (!token || !user?.buildingId) return;

    try {
      setLoading(true);
      const data = await apiClient.get<PricingRule[]>(`/pricing/${user.buildingId}`);
      setPricingRules(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch pricing rules');
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;

    try {
      await apiClient.register({ ...newUser, buildingId: user?.buildingId });

      setNewUser({ username: '', email: '', password: '', role: 'tenant' });
      setShowAddUser(false);
      fetchUsers();
      setError('');
    } catch (err: any) {
      setError(err.message || 'Failed to create user');
    }
  };

  const handleAddPricing = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !user?.buildingId) return;

    try {
      await apiClient.post('/pricing', { ...newPricing, buildingId: user.buildingId });

      setNewPricing({ dayOfWeek: 1, startTime: '09:00', endTime: '17:00', hourlyRate: 50 });
      setShowAddPricing(false);
      fetchPricingRules();
      setError('');
    } catch (err: any) {
      setError(err.message || 'Failed to create pricing rule');
    }
  };

  const handleDeletePricing = async (id: string) => {
    if (!token || !confirm('Are you sure you want to delete this pricing rule?')) return;

    try {
      await apiClient.delete(`/pricing/${id}`);

      fetchPricingRules();
      setError('');
    } catch (err: any) {
      setError(err.message || 'Failed to delete pricing rule');
    }
  };

  useEffect(() => {
    if (activeTab === 'users') {
      fetchUsers();
    } else {
      fetchPricingRules();
    }
  }, [activeTab, token, user?.buildingId]);

  return (
    <div className="ah-shell">
      <div className="ah-panel">
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div className="ah-card">
            <div style={{ marginBottom: 24 }}>
              <h1 className="ah-title">Admin Panel</h1>
              <p className="muted">Manage users and pricing for your building</p>
            </div>

            {/* Tab Navigation */}
            <div style={{ display: 'flex', gap: 16, marginBottom: 24, borderBottom: '1px solid var(--border)' }}>
              <button
                onClick={() => setActiveTab('users')}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '12px 0',
                  cursor: 'pointer',
                  fontSize: 16,
                  fontWeight: 500,
                  color: activeTab === 'users' ? 'var(--primary)' : 'var(--text)',
                  borderBottom: activeTab === 'users' ? '2px solid var(--primary)' : '2px solid transparent',
                }}
              >
                User Management
              </button>
              <button
                onClick={() => setActiveTab('pricing')}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '12px 0',
                  cursor: 'pointer',
                  fontSize: 16,
                  fontWeight: 500,
                  color: activeTab === 'pricing' ? 'var(--primary)' : 'var(--text)',
                  borderBottom: activeTab === 'pricing' ? '2px solid var(--primary)' : '2px solid transparent',
                }}
              >
                Pricing Rules
              </button>
            </div>

            {/* Error Display */}
            {error && (
              <div style={{
                color: '#e53e3e',
                backgroundColor: '#fed7d7',
                border: '1px solid #feb2b2',
                borderRadius: 6,
                padding: 16,
                marginBottom: 24
              }}>
                {error}
              </div>
            )}

            {/* Users Tab */}
            {activeTab === 'users' && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                  <h2>Users</h2>
                  <button onClick={() => setShowAddUser(true)} className="ah-btn">Add User</button>
                </div>

                {/* Add User Form */}
                {showAddUser && (
                  <div className="ah-card" style={{ marginBottom: 24, backgroundColor: 'var(--surface-hover)' }}>
                    <h3 style={{ marginBottom: 16 }}>Add New User</h3>
                    <form onSubmit={handleAddUser}>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 16 }}>
                        <input
                          type="text"
                          className="ah-input"
                          placeholder="Username"
                          value={newUser.username}
                          onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                          required
                        />
                        <input
                          type="email"
                          className="ah-input"
                          placeholder="Email"
                          value={newUser.email}
                          onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                          required
                        />
                        <input
                          type="password"
                          className="ah-input"
                          placeholder="Password"
                          value={newUser.password}
                          onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                          required
                        />
                        <select
                          className="ah-input"
                          value={newUser.role}
                          onChange={(e) => setNewUser({ ...newUser, role: e.target.value as 'admin' | 'manager' | 'tenant' })}
                        >
                          <option value="tenant">Tenant</option>
                          <option value="manager">Manager</option>
                          <option value="admin">Admin</option>
                        </select>
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button type="submit" className="ah-btn">Add User</button>
                        <button type="button" onClick={() => setShowAddUser(false)} className="ah-btn ah-btn-outline">Cancel</button>
                      </div>
                    </form>
                  </div>
                )}

                {/* Users Table */}
                {loading ? (
                  <div style={{ textAlign: 'center', padding: 40 }}>Loading users...</div>
                ) : (
                  <div className="ah-table-container">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Username</th>
                          <th>Email</th>
                          <th>Role</th>
                          <th>Created</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((user) => (
                          <tr key={user.id}>
                            <td>{user.username}</td>
                            <td>{user.email}</td>
                            <td>
                              <span style={{
                                background: user.role === 'admin' ? '#e53e3e' : user.role === 'manager' ? '#dd6b20' : '#38a169',
                                color: 'white',
                                padding: '2px 8px',
                                borderRadius: 12,
                                fontSize: 12,
                                fontWeight: 500
                              }}>
                                {user.role.toUpperCase()}
                              </span>
                            </td>
                            <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Pricing Tab */}
            {activeTab === 'pricing' && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                  <h2>Pricing Rules</h2>
                  <button onClick={() => setShowAddPricing(true)} className="ah-btn">Add Pricing Rule</button>
                </div>

                {/* Add Pricing Form */}
                {showAddPricing && (
                  <div className="ah-card" style={{ marginBottom: 24, backgroundColor: 'var(--surface-hover)' }}>
                    <h3 style={{ marginBottom: 16 }}>Add New Pricing Rule</h3>
                    <form onSubmit={handleAddPricing}>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 16, marginBottom: 16 }}>
                        <div>
                          <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 500 }}>Day</label>
                          <select
                            className="ah-input"
                            value={newPricing.dayOfWeek}
                            onChange={(e) => setNewPricing({ ...newPricing, dayOfWeek: parseInt(e.target.value) })}
                          >
                            {DAYS_OF_WEEK.map((day, index) => (
                              <option key={index} value={index}>{day}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 500 }}>Start Time</label>
                          <input
                            type="time"
                            className="ah-input"
                            value={newPricing.startTime}
                            onChange={(e) => setNewPricing({ ...newPricing, startTime: e.target.value })}
                            required
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 500 }}>End Time</label>
                          <input
                            type="time"
                            className="ah-input"
                            value={newPricing.endTime}
                            onChange={(e) => setNewPricing({ ...newPricing, endTime: e.target.value })}
                            required
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 500 }}>Hourly Rate ($)</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            className="ah-input"
                            value={newPricing.hourlyRate}
                            onChange={(e) => setNewPricing({ ...newPricing, hourlyRate: parseFloat(e.target.value) })}
                            required
                          />
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button type="submit" className="ah-btn">Add Rule</button>
                        <button type="button" onClick={() => setShowAddPricing(false)} className="ah-btn ah-btn-outline">Cancel</button>
                      </div>
                    </form>
                  </div>
                )}

                {/* Pricing Rules Table */}
                {loading ? (
                  <div style={{ textAlign: 'center', padding: 40 }}>Loading pricing rules...</div>
                ) : (
                  <div className="ah-table-container">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Day</th>
                          <th>Time Range</th>
                          <th style={{ textAlign: 'right' }}>Hourly Rate</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pricingRules.map((rule) => (
                          <tr key={rule.id}>
                            <td>{DAYS_OF_WEEK[rule.dayOfWeek]}</td>
                            <td>{rule.startTime} - {rule.endTime}</td>
                            <td style={{ textAlign: 'right' }}>${rule.hourlyRate.toFixed(2)}</td>
                            <td>
                              <button 
                                onClick={() => handleDeletePricing(rule.id)} 
                                style={{
                                  background: '#e53e3e',
                                  color: 'white',
                                  border: 'none',
                                  padding: '4px 8px',
                                  borderRadius: 4,
                                  cursor: 'pointer',
                                  fontSize: 12
                                }}
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {pricingRules.length === 0 && !loading && (
                  <div style={{ textAlign: 'center', padding: 40 }}>
                    <div className="muted">No pricing rules configured. Add some rules to enable cost calculations.</div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPage;